import styled from '@emotion/styled';
import {
  Button as _Button,
  DialogActions as _DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  useMediaQuery,
} from '@material-ui/core';
import { createElement, MouseEventHandler, SyntheticEvent } from 'react';
import { FormattedMessage } from 'react-intl';

import ProductPricing from '../../../entity/ProductPricing';
import { ProjectTypeForListFragment } from '../../../generated/graphql';
import theme from '../../../theme';
import { itemBackground } from '../../constant/color';
import { SPACING_REGULAR, SPACING_UNIT } from '../../constant/spacing';
import mq from '../../styleUtility/mediaquery';
import Dialog from '../primitive/DialogWithPadding';
import LfToBr from '../primitive/LfToBr';
import VideoScreen from '../primitive/VideoScreen';

interface Props {
  projectType: ProjectTypeForListFragment;
  open: boolean;
  isPointRequired: boolean;
  productPricing?: ProductPricing;
  onAccept: MouseEventHandler<HTMLElement>;
  onRequestClose: (e: SyntheticEvent<HTMLElement>) => void;
  className?: string;
}

const ProjectTypeDetailDialog = ({ projectType, open, onAccept, onRequestClose, className }: Props) => {
  const isPC = useMediaQuery(`(min-width:${theme.breakpoints.values['lg']}px)`);
  let preview;

  if (projectType.sampleVideoUrl) {
    preview = <StyledVideo src={projectType.sampleVideoUrl} loop autoPlay />;
  } else if (projectType.thumbnailImageUrl) {
    preview = <StyledImage src={projectType.thumbnailImageUrl} />;
  } else {
    preview = <StyledImage src="/assets/empty_thumbnail.png" />;
  }

  return (
    <Dialog open={open} onRequestClose={onRequestClose} className={className} maxWidth="md" fullScreen={!isPC}>
      <DialogTitle>{projectType.title}</DialogTitle>

      <Content>
        <PreviewContainer>{preview}</PreviewContainer>

        {projectType.description ? (
          <Description variant="body2">
            <LfToBr text={projectType.description} />
          </Description>
        ) : null}
      </Content>

      <DialogActions>
        <Button color="default" onClick={onRequestClose}>
          <FormattedMessage id="project_type_detail_dialog.close" />
        </Button>

        <Button onClick={onAccept}>
          <FormattedMessage id="project_type_detail_dialog.start_project" />
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const Content = styled(DialogContent)({
  width: '100%',
});

const Description = styled(Typography)({
  marginBottom: SPACING_REGULAR,
});

const PreviewContainer = styled('div')({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'flex-start',
  marginBottom: SPACING_UNIT * 3,
  backgroundColor: itemBackground,
  border: `1px solid ${itemBackground}`,
});

const StyledVideo = styled(VideoScreen)({
  minWidth: 200,
  maxWidth: 600,
  minHeight: 200,
  maxHeight: '50vh',
  marginBottom: -1, // To hide an odd bottom border
});

const StyledImage = styled('img')({
  minWidth: 200,
  maxWidth: '100%',
  minHeight: 200,
  maxHeight: '50vh',
});

const DialogActions = styled(_DialogActions)({
  [mq('sp')]: {
    flexDirection: 'column-reverse',
    width: '100%',
  },
});

const Button = styled(_Button)({
  [mq('sp')]: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column-reverse',
    marginTop: SPACING_REGULAR,
    ':not(:first-of-type)': {
      marginLeft: 0,
    },
    '> button': {
      marginBottom: SPACING_UNIT,
      marginLeft: 0,
    },
  },
});

export default ProjectTypeDetailDialog;
